/**
 * Comprobaciones diarias de la clínica (2026-09-06, dirección: «es prioritario
 * en el día a día y ha habido fallos»).
 *
 * El servidor recorre cada día laborable, como paciente, todos los enlaces
 * que manda la clínica (scripts/comprobar_enlaces.py) y deja el resultado en
 * GET /api/salud/enlaces. Aquí se decide si la campana tiene que ponerse en
 * rojo: cuando la última ejecución falló, o cuando no hay ninguna de los
 * últimos DOS días laborables (el cron corre de lunes a viernes a las 07:30;
 * un día sin comprobar puede ser que aún no le toque, dos ya es que algo se
 * ha parado).
 */

function fechaLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}

/** Los últimos `n` días laborables (L–V) hasta hoy incluido, como YYYY-MM-DD. */
export function diasLaborablesRecientes(ahora = new Date(), n = 2) {
  const dias = [];
  const d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  while (dias.length < n) {
    const semana = d.getDay(); // 0 domingo … 6 sábado
    if (semana !== 0 && semana !== 6) dias.push(fechaLocal(d));
    d.setDate(d.getDate() - 1);
  }
  return dias;
}

export function fechaCorta(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
    + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/**
 * El aviso de UNA comprobación diaria, con el nombre que lleva delante.
 *
 * Lo comparten los dos vigilantes (2026-09-10): el de los ENLACES DE
 * PACIENTES, que recorre la clínica desde fuera con los ojos del paciente, y
 * el del DÍA DE CONSULTA, que la recorre por dentro con los de recepción. Las
 * dos respuestas tienen la misma forma ({ ultima, anteriores, en_marcha }) y
 * la regla del rojo es la misma, así que la lógica es una sola.
 *
 * @param {object|null} datos  la respuesta del endpoint de estado
 * @param {string} etiqueta    «Enlaces de pacientes» / «Día de consulta»
 * @param {Date} ahora
 * @returns {null | { tipo: 'fallo'|'sin_comprobar', texto: string }}
 */
export function avisoDeComprobacion(datos, etiqueta, ahora = new Date()) {
  const ultima = datos && typeof datos === 'object' && !Array.isArray(datos) ? datos.ultima : null;
  if (!ultima) {
    return { tipo: 'sin_comprobar', texto: `${etiqueta}: sin comprobar todavía` };
  }
  if (ultima.ok === false) {
    const fallos = (ultima.checks || []).filter((c) => c && c.ok === false).map((c) => c.nombre);
    const primero = fallos[0] || 'la comprobación';
    const resto = fallos.length > 1 ? ` y ${fallos.length - 1} más` : '';
    return { tipo: 'fallo', texto: `${etiqueta}: fallo en ${primero}${resto} (${fechaCorta(ultima.at)})` };
  }
  const recientes = diasLaborablesRecientes(ahora, 2);
  const fecha = ultima.at ? fechaLocal(new Date(ultima.at)) : '';
  // Anterior a los dos últimos días laborables (ni hoy ni el laborable
  // previo): el cron se ha parado.
  if (!fecha || fecha < recientes[recientes.length - 1]) {
    return { tipo: 'sin_comprobar', texto: `${etiqueta}: sin comprobar desde ${fechaCorta(ultima.at)}` };
  }
  return null;
}

/** GET /api/salud/enlaces — el recorrido del PACIENTE (scripts/comprobar_enlaces.py). */
export function avisoEnlacesPacientes(datos, ahora = new Date()) {
  return avisoDeComprobacion(datos, 'Enlaces de pacientes', ahora);
}

/**
 * GET /api/salud/recepcion — el recorrido de RECEPCIÓN
 * (scripts/comprobar_recepcion.py, 2026-09-10, dirección: «como si pasara un día
 * de consulta viendo las cosas… y si hay alguno informa rápido»).
 */
export function avisoDiaDeConsulta(datos, ahora = new Date()) {
  return avisoDeComprobacion(datos, 'Día de consulta', ahora);
}
